"use client";

import clsx from "clsx";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Icon, {ICON_SIZES, ICON_TYPE} from "@/components/library/Icon";

const COPY_ACK_MS = 1500;

/**
 * Lightweight copy button for the file-bubble trailing row. Copies the raw
 * markdown/text of the paired assistant reply. Kept intentionally simpler
 * than the Copy dropdown used on text bubbles: the file bubble doesn't have a
 * rendered HTML container to feed Copy's formatting variants.
 */
const CopyTextButton: React.FC<{text: string}> = ({text}) => {
   const {t: ct} = useTranslation("common");
   const [copied, setCopied] = useState(false);
   const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   useEffect(() => {
      return () => {
         if (timerRef.current) clearTimeout(timerRef.current);
      };
   }, []);

   const handleCopy = async () => {
      try {
         await navigator.clipboard.writeText(text);
         setCopied(true);
         if (timerRef.current) clearTimeout(timerRef.current);
         timerRef.current = setTimeout(() => setCopied(false), COPY_ACK_MS);
      } catch (err) {
         console.error("Copy failed:", err);
      }
   };

   return (
      <button
         type="button"
         onClick={handleCopy}
         aria-label={ct("copy", {defaultValue: "Copy"})}
         title={copied ? ct("copied", {defaultValue: "Copied"}) : ct("copy", {defaultValue: "Copy"})}
         className={clsx(
            "inline-flex items-center justify-center size-8 rounded-lg",
            "opacity-70 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10",
            "transition-all duration-150 text-600"
         )}
      >
         <Icon
            icon={copied ? "check" : "copy"}
            size={ICON_SIZES.SM}
            type={ICON_TYPE.REGULAR}
         />
      </button>
   );
};

const FileBubbleActions: React.FC<{
   copyText?: string;
   feedbackSlot?: React.ReactNode;
}> = ({copyText, feedbackSlot}) => {
   if (!copyText && !feedbackSlot) return null;
   return (
      <div className="mt-2 flex items-center gap-1 text-600">
         {copyText && <CopyTextButton text={copyText} />}
         {feedbackSlot}
      </div>
   );
};

export default FileBubbleActions;
